import TransportTimeline from "@components/common/Timeline/TransportTimeline";
import { useTranslation } from "react-i18next";
import i18n from "@libs/i18n";
import { ReactNode, useMemo } from "react";
import { CabinClass } from "../search/Search";
import { availableCabinClasses } from "@services/travel";
import FloatingTooltip from "@components/common/FloatingTooltip";

import {
  Offer,
  Price,
  TrainCabinClass,
  Transportation,
} from "@gen/travel/v1/common_pb";
import { Solution } from "@gen/travel/v1/search_pb";
import { useDAP } from "../dap.hook";
import { ExchangeOffer, ExchangeSolution } from "@gen/travel/v1/exchange_pb";
import { DAP } from "@gen/dap/v1/dap_pb";

interface SolutionItemProps {
  solution: Solution | ExchangeSolution;
  dap: DAP;
  index: number;
  selectedClass?: CabinClass;
  selectedTransport: Transportation;
  isFilteredSolution: (
    solution: Solution | ExchangeSolution,
    cabinClass: CabinClass,
  ) => boolean;
  isFilteredOffer: (offer: Offer | ExchangeOffer) => boolean;
  onSolutionSelect: (
    solution: Solution | ExchangeSolution,
    cabinClass: CabinClass,
    index: number,
  ) => void;
}

export function SolutionItem({
  dap,
  solution,
  index,
  selectedClass,
  selectedTransport,
  isFilteredSolution,
  isFilteredOffer,
  onSolutionSelect,
}: SolutionItemProps) {
  const { t } = useTranslation();
  const isPlane = selectedTransport == Transportation.PLANE;
  const cheapestClass = useMemo(() => {
    return (
      [...solution.offers]
        .sort(
          (a, b) =>
            Number(a.initialPrice?.amount) - Number(b.initialPrice?.amount),
        )
        .at(0)?.cabinClass?.value || TrainCabinClass.SECOND
    );
  }, [solution]);

  if (!solution.journey) {
    return;
  }

  function cheapestOfferPrice(cabinClass: CabinClass): string {
    if (!isFilteredSolution(solution, cabinClass)) {
      return t("results.unavailable");
    }

    const offersClass = solution.offers.filter(
      (offer) => offer.cabinClass?.value === cabinClass,
    );

    const offer = offersClass
      .filter((o) => isFilteredOffer(o))
      .sort(
        (a, b) =>
          Number(a.initialPrice?.amount) - Number(b.initialPrice?.amount),
      )
      .at(0);

    if (!offer || !offer.initialPrice) {
      return t("results.unavailable");
    }
    if ("priceDelta" in offer) {
      return formatPriceDelta(offer.priceDelta);
    }

    return formatPrice(offer.initialPrice);
  }

  function formatPriceDelta(price: Price | undefined): string {
    const amount = Number(price?.amount);
    const sign = amount > 0 ? "+" : amount < 0 ? "−" : "";
    return (
      sign +
      new Intl.NumberFormat(i18n.language, {
        style: "currency",
        currency: price?.currency,
        minimumFractionDigits: 2,
      }).format(Math.abs(amount) / 100)
    );
  }

  function formatPrice(price: Price): string {
    return new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: price.currency,
      minimumFractionDigits: 2,
    }).format(Number(price.amount) / 100);
  }

  return (
    <div
      data-testid="solution"
      className="border border-cnam-light-300 bg-white rounded-lg px-4 py-6 flex justify-between gap-6"
    >
      <TransportTimeline journey={solution?.journey} ghg />
      <div className="flex shrink-0 items-center space-x-4">
        <div className={`hidden ${isPlane ? "2xl:flex" : "xl:flex"} space-x-4`}>
          {availableCabinClasses(dap, solution).map((cabinClass) => (
            <CompliancyWrapper
              key={cabinClass}
              solution={solution}
              cabinClass={cabinClass}
            >
              <button
                data-testid={`solution-class-${cabinClass}`}
                disabled={!isFilteredSolution(solution, cabinClass)}
                className={`relative w-[96px] h-[52px] relativeflex items-center justify-center p-2 rounded-lg font-bold cursor-pointer
                          hover:brightness-90 disabled:hover:brightness-100 disabled:text-gray-800 disabled:cursor-not-allowed
                          ${
                            selectedClass === cabinClass
                              ? "bg-cnam-blue text-white"
                              : "bg-neutral-50 border-gray-300"
                          }`}
                onClick={() => onSolutionSelect(solution, cabinClass, index)}
              >
                {" "}
                {cheapestOfferPrice(cabinClass)}
              </button>
            </CompliancyWrapper>
          ))}
        </div>
        <div
          className={`p-2 w-[96px] h-[52px] relative flex ${
            isPlane ? "2xl:hidden" : "xl:hidden"
          } items-center justify-center border rounded cursor-pointer ${
            selectedClass
              ? "border-blue-500 bg-cnam-blue text-white"
              : "border-gray-300"
          }`}
          onClick={() => onSolutionSelect(solution, cheapestClass, index)}
        >
          <div className="text-xs absolute -top-5 text-text-strong">
            {t("results.startingFrom")}
          </div>
          <div className="font-bold">{cheapestOfferPrice(cheapestClass)}</div>
        </div>
      </div>
    </div>
  );
}

function CompliancyWrapper({
  solution,
  cabinClass,
  children,
}: {
  solution: Solution | ExchangeSolution;
  cabinClass: CabinClass;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { dap } = useDAP();

  function isCompliant() {
    return solution.offers
      .filter((o) => o.cabinClass.value === cabinClass)
      .some((o) => o.isCompliant);
  }

  if (dap.isComplianceFilterEnabled) {
    return <>{children}</>;
  }

  return (
    <FloatingTooltip content={isCompliant() ? t("conform") : t("notConform")}>
      <>
        {children}
        <div
          className={`absolute top-2 right-2 w-[8px] h-[8px] rounded-full ${isCompliant() ? "bg-secondary-soft" : "bg-negative-soft"}`}
        ></div>
      </>
    </FloatingTooltip>
  );
}
